
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import React, { memo, useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { shallowEqual } from 'react-redux';
import { css } from '@emotion/react';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import ChatPlayProvider from '@provider/ChatPlayProvider';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { setPlayChapter, clearPlayChapter } from '@/slices/chatChapterSlice';
import { RootState } from '@store/store';
import { GET_CHAT_CHAPTER, LIST_ACHIEVEMENTS } from '@api/chapter';
import { GET_USER } from '@api/user';
import { getAdultCheckType } from '@common/utils';
import { SERVER_ERROR_CODE } from '@common/values';
import { ListAchievements, PlayChapter } from '@customTypes/chapter';
import { GetUser } from '@customTypes/user';
import ChatPlayDataProvider from '@provider/ChatPlayDataProvider';
import SPLoading from '@components/commons/loading/SPLoading';
import AdvertisementPopup from '@components/commons/popup/AdvertisementPopup';
import ChatStory from '@/components/chatStory/story/ChatStory';
import useCheckAdByRemoteConfig from '@/hooks/useCheckAdByRemoteConfig';
import { startChapterFromDetail, startStoryAll } from '@lib/eventManager';
import * as Sentry from '@sentry/nextjs';

const ChatStoryPage = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { chapterId, prevScreen } = router.query;
  const [isAdLoading, setIsAdLoading] = useState<boolean>(true);
  const startChapterTransaction = useRef<any>();
  const getChapterSpanRef = useRef<any>();
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { isRestarting } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  const { data: userData } = useQuery<GetUser>(GET_USER, {
    fetchPolicy: 'network-only',
  });

  const onChapterError = (error: ApolloError) => {
    router.replace({ pathname: '/error' });
  };

  const { data: chapterData, loading: chapterLoading } = useQuery<{
    chapter: PlayChapter;
  }>(GET_CHAT_CHAPTER, {
    variables: {
      chapterId: Number(chapterId),
    },
    skip: !chapterId,
    fetchPolicy: 'no-cache',
    onError: onChapterError,
  });

  const [achievementsMutate, { loading: achievementsLoading }] = useLazyQuery<{
    listAchievements: ListAchievements;
  }>(LIST_ACHIEVEMENTS, {
    fetchPolicy: 'network-only',
  });

  const { isShowAd, setIsShowAd } = useCheckAdByRemoteConfig({
    storyId: chapterData?.chapter.storyId,
    chapterId: chapterData?.chapter.chapterId,
  });

  useEffect(() => {
    if (!chapterData) return;

    getChapterSpanRef.current?.finish();

    updateAchievements();
  }, [chapterData]);

  useEffect(() => {
    const transaction = Sentry.startTransaction({
      name: 'StartChapter',
      description:
        '작품 모듈 진입 -> 작품 로드 후 인물 설정 팝업 또는 첫 채팅 시작',
      data: {
        chapterId,
      },
    });
    getChapterSpanRef.current = transaction.startChild({
      description: 'getChapter 요청',
      op: 'GetChapter',
    });
    startChapterTransaction.current = transaction;
  }, []);

  const updateAchievements = async () => {
    if (chapterData) {
      const storyId = chapterData.chapter.storyId;
      try {
        const updateAchievementsSpan =
          startChapterTransaction.current?.startChild({
            op: 'UpdateAchievements',
            description: 'updateAchievements 요청',
          });

        const res = await achievementsMutate({
          variables: {
            storyId,
          },
        });
        updateAchievementsSpan.finish();
        initData(res.data?.listAchievements);
      } catch (error: unknown) {
        const e = error as ApolloError;
        if (e.message.includes(SERVER_ERROR_CODE.UNREGISTER)) {
          initData();
        } else {
          router.replace({ pathname: '/error' });
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearPlayChapter());
      setIsShowAd(undefined);
    };
  }, [chapterId]);

  useEffect(() => {
    if (chapterData) {
      startStoryAll(prevScreen as string | undefined);
    }

    if (prevScreen === 'story_detail' && chapterData) {
      const userPlayedChapters = playChapterInfo?.playChapter.story.lastPlay
        ? playChapterInfo?.playChapter.story.lastPlay?.playedChapters
        : [];
      const playingChapterIds = userPlayedChapters?.map(
        (chapter) => chapter.chapterId,
      );
      const isPlaying = playingChapterIds.some(
        (id) => id === chapterData.chapter.chapterId,
      );

      startChapterFromDetail({
        storyId: chapterData.chapter.storyId,
        storyBundleId: chapterData.chapter.story.bundle?.storyBundleId,
        chapterId: chapterData.chapter.chapterId,
        chapterIndex: chapterData.chapter.chapterIndex,
        storyName: chapterData.chapter.story.name,
        storyLanguage: chapterData.chapter.story.language?.code,
        storyGenre: chapterData.chapter.story.genreKey?.type,
        playType: 'interactive',
        startType: isRestarting
          ? 'time_leap'
          : isPlaying
          ? 'continue'
          : 'start',
        chapterIsAdult: chapterData.chapter.isAdult,
      });

      delete router.query['prevScreen'];
    }
  }, [prevScreen, chapterData]);

  const initData = (listAchievements?: ListAchievements) => {
    if (chapterData) {
      const chapterInfo = chapterData.chapter;
      const checkType = getAdultCheckType({
        isAdultType: chapterInfo.story.isAdult,
        userData: userData,
      });

      if (checkType === 'auth') {
        router.push(`/auth/adult?storyId=${chapterInfo.storyId}`);
      } else if (checkType === 'adult') {
        router.replace(`/adult?storyId=${chapterInfo.storyId}`);
      } else {
        dispatch(
          setPlayChapter({
            playChapterInfo: chapterInfo,
            achievements: listAchievements,
          }),
        );
      }
    }
  };

  if (chapterLoading || achievementsLoading) {
    return <SPLoading />;
  }

  return (
    <ChatPlayDataProvider>
      <ChatPlayProvider>
        <div css={container}>
          {playChapterInfo && !isAdLoading && isShowAd === false ? (
            <ChatStory
              startChapterTransaction={startChapterTransaction.current}
            />
          ) : (
            <>
              {chapterData && (
                <AdvertisementPopup
                  isShow={isShowAd}
                  onLoading={setIsAdLoading}
                  startChapterTransaction={startChapterTransaction.current}
                  onFinish={() => setIsShowAd(false)}
                  fallbackUrl={`/story/${chapterData.chapter.storyId}`}
                  chapterData={{
                    storyId: chapterData.chapter.storyId,
                    storyBundleId:
                      chapterData.chapter.story.bundle?.storyBundleId,
                    chapterId: chapterData.chapter.chapterId,
                    chapterIndex: chapterData.chapter.chapterIndex,
                    storyName: chapterData.chapter.story.name,
                    storyLanguage: chapterData.chapter.story.language?.code,
                    storyGenre: chapterData.chapter.story.bundle?.genre,
                    playType: chapterData.chapter.story.playType,
                  }}
                  isPurchasableStory={chapterData.chapter.story.purchasable}
                  rewardType="FREE_CHAPTER"
                />
              )}
            </>
          )}
          {isAdLoading && <SPLoading />}
        </div>
      </ChatPlayProvider>
    </ChatPlayDataProvider>
  );
};

const container = css`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const __Page_Next_Translate__ = memo(ChatStoryPage);


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
    });
// @ts-ignore
  